/**
 * IM 实时连接引擎（story AC-3）。框架无关、可注入 socket 工厂与时钟,便于单测。
 * 复刻源 App ImEngine 语义（见 story Dev Notes port-risk）:
 *  - 连接态 CONNECTED 由收到 CONNECTED 帧驱动,而非 socket.onopen。
 *  - 心跳 30s PING（服务端 60s 空闲必关）。
 *  - 断线指数退避重连;每次重连前刷新 token;默认无限重连。
 *  - 握手 401 / 运行期 ERROR(040001) → 刷新 token 重连;刷新失败 → CLOSED。
 *  - FORCE_LOGOUT(040003) → CLOSED 不重连。
 *
 * 健壮性（code-review 加固）:
 *  - socket 事件按“当前 socket 身份”守卫:onerror+onclose 双触发只处理一次,
 *    旧 socket 的迟到事件不会误伤新 socket。
 *  - 握手后迟迟收不到 CONNECTED 帧 → 连接看门狗超时触发重连(避免永久卡 CONNECTING)。
 *  - refreshAndReconnect 有可重入守卫,避免并发刷新/并发建连。
 */
import {
  decodeFrame,
  encodeFrame,
  pingFrame,
  chatFrame,
  disconnectFrame,
  appStateFrame,
  IM_SUBPROTOCOL,
  ImCode,
  type ImFrame,
} from './frame';
import { computeBackoffDelay } from './backoff';

export type ImConnectionState =
  | 'DISCONNECTED'
  | 'CONNECTING'
  | 'CONNECTED'
  | 'RECONNECTING'
  | 'CLOSED';

/** 注入用的最小 socket 接口（浏览器 WebSocket 兼容）。 */
export interface ImSocket {
  send(data: string): void;
  close(code?: number, reason?: string): void;
  onopen: (() => void) | null;
  onmessage: ((ev: { data: unknown }) => void) | null;
  onerror: (() => void) | null;
  onclose: (() => void) | null;
}

export interface ImConnectionConfig {
  /** 实时读取会话凭据（userId 必须等于 token sub）。 */
  getCredentials: () => { userId?: string; token?: string };
  /** 刷新 access token（写回 store 并返回新 token;失败返回 null）。 */
  refreshToken: () => Promise<string | null>;
  onState: (state: ImConnectionState) => void;
  /** 业务/同步帧回调（PONG/CONNECTED 等控制帧已内部消费,不回调）。 */
  onFrame: (frame: ImFrame) => void;
  socketFactory?: (url: string, protocol: string) => ImSocket;
  /** ws(s)://host 基址;缺省由 location 推导（测试可覆盖）。 */
  wsBaseOverride?: string;
  heartbeatIntervalMs?: number;
  /** 握手后等待 CONNECTED 帧的超时(ms);超时视为断线重连。 */
  connectTimeoutMs?: number;
  /** 0 = 无限重连（默认）。 */
  maxReconnectAttempts?: number;
  now?: () => number;
  rand?: () => number;
}

function defaultWsBase(): string {
  const loc = (globalThis as { location?: { protocol: string; host: string } }).location;
  if (!loc) return 'ws://localhost';
  const proto = loc.protocol === 'https:' ? 'wss:' : 'ws:';
  return `${proto}//${loc.host}`;
}

export class ImConnection {
  private cfg: Required<
    Pick<ImConnectionConfig, 'heartbeatIntervalMs' | 'maxReconnectAttempts' | 'connectTimeoutMs'>
  > &
    ImConnectionConfig;
  private socket: ImSocket | null = null;
  private state: ImConnectionState = 'DISCONNECTED';
  private heartbeatTimer: ReturnType<typeof setInterval> | null = null;
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  private connectTimer: ReturnType<typeof setTimeout> | null = null;
  private reconnectAttempt = 0;
  private userClosed = false;
  private refreshing = false;

  constructor(config: ImConnectionConfig) {
    this.cfg = {
      heartbeatIntervalMs: 30_000,
      maxReconnectAttempts: 0,
      connectTimeoutMs: 20_000,
      ...config,
    };
  }

  getState(): ImConnectionState {
    return this.state;
  }

  /** 建立连接（首次或外部触发）。凭据缺失则置 CLOSED。 */
  connect(): void {
    this.userClosed = false;
    this.openSocket();
  }

  private openSocket(): void {
    const { userId, token } = this.cfg.getCredentials();
    if (!userId || !token) {
      this.setState('CLOSED');
      return;
    }
    this.setState(this.reconnectAttempt > 0 ? 'RECONNECTING' : 'CONNECTING');
    const base = this.cfg.wsBaseOverride ?? defaultWsBase();
    const url = `${base}/ws/im/${encodeURIComponent(userId)}?token=${encodeURIComponent(token)}`;
    const factory =
      this.cfg.socketFactory ??
      ((u: string, p: string) => new WebSocket(u, p) as unknown as ImSocket);
    let sock: ImSocket;
    try {
      sock = factory(url, IM_SUBPROTOCOL);
    } catch {
      this.scheduleReconnect();
      return;
    }
    this.socket = sock;
    // 注意:onopen 不置 CONNECTED —— 等服务端 CONNECTED 帧（port-risk #1）。
    sock.onopen = () => {
      /* 等 CONNECTED 帧 */
    };
    sock.onmessage = (ev) => this.handleMessage(ev.data);
    // 按 socket 身份守卫:onerror + onclose 双触发只处理一次;旧 socket 迟到事件不误伤新 socket。
    sock.onerror = () => this.onSocketDown(sock);
    sock.onclose = () => this.onSocketDown(sock);
    // 连接看门狗:握手后迟迟无 CONNECTED 帧 → 主动断开重连,避免永久卡 CONNECTING。
    this.armConnectWatchdog(sock);
  }

  private armConnectWatchdog(sock: ImSocket): void {
    this.clearConnectTimer();
    this.connectTimer = setTimeout(() => {
      this.connectTimer = null;
      // 仍是当前 socket 且尚未 CONNECTED → 视为握手失败,走断线重连。
      if (sock === this.socket && this.state !== 'CONNECTED') {
        this.dropCurrentSocket();
        this.scheduleReconnect();
      }
    }, this.cfg.connectTimeoutMs);
  }

  private handleMessage(data: unknown): void {
    if (typeof data !== 'string') return;
    const frame = decodeFrame(data);
    if (!frame) return; // 容错丢弃

    switch (frame.type) {
      case 'PONG':
        return; // 心跳存活,无需上抛
      case 'CONNECTED':
        this.reconnectAttempt = 0;
        this.clearConnectTimer();
        this.setState('CONNECTED');
        this.startHeartbeat();
        return;
      case 'FORCE_LOGOUT':
        // 他端抢连:清理,置 CLOSED,不重连。
        this.userClosed = true;
        this.cleanup();
        this.setState('CLOSED');
        this.cfg.onFrame(frame);
        return;
      case 'DISCONNECTED':
        this.cleanup();
        this.setState('CLOSED');
        return;
      case 'ERROR':
        if (frame.code === ImCode.HANDSHAKE_UNAUTHORIZED) {
          // token 失效:丢弃当前连接,走退避重连(内部刷新 token),避免不上抛为聊天错误、
          // 且经 scheduleReconnect 得到退避 + 上限保护,防止紧循环。
          this.dropCurrentSocket();
          this.scheduleReconnect();
          return;
        }
        this.cfg.onFrame(frame); // 040201/040202/040203/040206 → 聊天层标记失败
        return;
      default:
        // ACK / MESSAGE / SYNC_* 等 → 上抛
        this.cfg.onFrame(frame);
    }
  }

  /** 当前 socket 断开（onerror/onclose/看门狗）。按身份守卫,幂等。 */
  private onSocketDown(sock: ImSocket): void {
    if (sock !== this.socket) return; // 陈旧/重复事件
    this.detachSocket(sock);
    this.socket = null;
    this.clearHeartbeat();
    this.clearConnectTimer();
    if (this.userClosed) return;
    this.scheduleReconnect();
  }

  /** 主动丢弃当前 socket（不重连,由调用方决定后续）。 */
  private dropCurrentSocket(): void {
    const sock = this.socket;
    if (!sock) return;
    this.detachSocket(sock);
    this.socket = null;
    this.clearHeartbeat();
    this.clearConnectTimer();
    try {
      sock.close();
    } catch {
      /* ignore */
    }
  }

  private detachSocket(sock: ImSocket): void {
    sock.onopen = sock.onmessage = sock.onerror = sock.onclose = null;
  }

  private scheduleReconnect(): void {
    if (this.userClosed) return;
    const max = this.cfg.maxReconnectAttempts;
    if (max > 0 && this.reconnectAttempt >= max) {
      this.setState('CLOSED');
      return;
    }
    this.setState('RECONNECTING');
    const delay = computeBackoffDelay(this.reconnectAttempt, this.cfg.rand);
    this.reconnectAttempt += 1;
    if (this.reconnectTimer) clearTimeout(this.reconnectTimer);
    this.reconnectTimer = setTimeout(() => {
      this.reconnectTimer = null;
      void this.refreshAndReconnect();
    }, delay);
  }

  private async refreshAndReconnect(): Promise<void> {
    if (this.userClosed || this.refreshing) return;
    this.refreshing = true;
    let newToken: string | null;
    try {
      newToken = await this.cfg.refreshToken();
    } catch {
      newToken = null;
    } finally {
      this.refreshing = false;
    }
    if (this.userClosed) return;
    if (!newToken) {
      this.userClosed = true;
      this.cleanup();
      this.setState('CLOSED'); // TOKEN_EXPIRED
      return;
    }
    this.openSocket();
  }

  private startHeartbeat(): void {
    this.clearHeartbeat();
    this.heartbeatTimer = setInterval(() => {
      const now = this.cfg.now ? this.cfg.now() : Date.now();
      this.rawSend(pingFrame(now));
    }, this.cfg.heartbeatIntervalMs);
  }

  private clearHeartbeat(): void {
    if (this.heartbeatTimer) {
      clearInterval(this.heartbeatTimer);
      this.heartbeatTimer = null;
    }
  }

  private clearConnectTimer(): void {
    if (this.connectTimer) {
      clearTimeout(this.connectTimer);
      this.connectTimer = null;
    }
  }

  private rawSend(frame: ImFrame): boolean {
    if (!this.socket) return false;
    try {
      this.socket.send(encodeFrame(frame));
      return true;
    } catch {
      return false;
    }
  }

  /** 发送一条聊天消息（仅 CONNECTED 态）。返回是否已写出。 */
  sendChat(clientMsgId: string, conversationId: string, content: string): boolean {
    if (this.state !== 'CONNECTED') return false;
    const now = this.cfg.now ? this.cfg.now() : Date.now();
    return this.rawSend(chatFrame(clientMsgId, conversationId, content, now));
  }

  /** 上报前后台（可选,无 ACK）。 */
  notifyAppState(state: 'FOREGROUND' | 'BACKGROUND'): void {
    if (this.state === 'CONNECTED') this.rawSend(appStateFrame(state));
  }

  /** 用户主动断开（优雅 DISCONNECT + 清理）。 */
  disconnect(): void {
    this.userClosed = true;
    if (this.socket) this.rawSend(disconnectFrame());
    this.cleanup();
    this.setState('CLOSED');
  }

  private cleanup(): void {
    this.clearHeartbeat();
    this.clearConnectTimer();
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
    if (this.socket) {
      const s = this.socket;
      this.detachSocket(s);
      try {
        s.close();
      } catch {
        /* ignore */
      }
      this.socket = null;
    }
  }

  private setState(next: ImConnectionState): void {
    if (this.state === next) return;
    this.state = next;
    this.cfg.onState(next);
  }
}
